import "./style.scss";

import { Tooltip } from "@mui/material";
import ChannelCession from "assets/img/svg/ChannelCession";
import ChannelCollective from "assets/img/svg/ChannelCollective";
import ChannelNumerique from "assets/img/svg/ChannelNumerique";
import ChannelOther from "assets/img/svg/ChannelOther";
import ChannelPhysique from "assets/img/svg/ChannelPhysique";
import AutocompleteFormComponent from "components/shared/FormComponent/AutocompleteFormComponent";
import StyledButton from "components/shared/StyledButton";
import { featureFlags } from "config/featureFlags";
import type { IChannel } from "containers/Channels/types";
import { truncateString } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import type { SetStateAction } from "react";
import type { UseFieldArrayAppend } from "react-hook-form";
import { useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { addDefaultEntry } from "store/priceHistory/priceHistoryStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { selectTitleDefaultPrice } from "store/titleStore";
import { ChannelCalculationMethod } from "store/types";

import type { OverriddenChannel, TFormCreateTitle } from "../types";

interface IAddSpecificChannel {
    close: () => void;
    append: UseFieldArrayAppend<TFormCreateTitle, "overriddenChannelsData">;
    addedChannels: number[];
    setActiveChannels: React.Dispatch<SetStateAction<number[]>>;
}

const AddSpecificChannel = ({ close, append, addedChannels, setActiveChannels }: IAddSpecificChannel) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { channels } = useAppSelector((state) => state.channels);
    const titleId = useAppSelector((state) => state.titles.title?.id);
    const { companyCurrency } = useCompanySettings();
    const defaultTitlePrice = useAppSelector(selectTitleDefaultPrice);

    const { setValue } = useFormContext<TFormCreateTitle>();

    const { watch, control } = useForm({
        defaultValues: {
            selectedChannel: { name: "", id: null },
        },
    });

    const watchChannel = watch("selectedChannel");

    const nonAddedChannels = channels.filter((channel) => !addedChannels.includes(channel.id));

    const onSubmitNewChannel = () => {
        const newChannel = nonAddedChannels.find((channel: IChannel) => channel.id === watchChannel.id);
        if (newChannel) {
            const newChannelData: OverriddenChannel = {
                channelName: newChannel.name,
                ean13: "",
                name: "",
                releaseDate: null,
                channelId: newChannel.id,
                kind: newChannel.kind,
                calculationMethod: newChannel.calculationMethod,
            };
            append(newChannelData);
            setActiveChannels((prevChannels) => [...prevChannels, newChannel.id]);
            // Add default price history entry for this channel
            setValue("shallRecompute", true, { shouldDirty: true });
            if (featureFlags.priceHistory && newChannel.calculationMethod !== ChannelCalculationMethod.SUBSCRIPTION) {
                dispatch(
                    addDefaultEntry({
                        channelId: newChannel.id,
                        titleId: titleId,
                        price: defaultTitlePrice,
                        currency: companyCurrency,
                    }),
                );
            }
        }
        close();
    };

    const renderChannelsOptions = (props: any, option: any) => {
        return (
            <li {...props} key={option.id} className="add-specific-channel-options-list">
                <div className="channel-name">
                    {option?.kind === 1 ? (
                        <ChannelPhysique color="#141516" />
                    ) : option?.kind === 2 ? (
                        <ChannelNumerique color="#141516" />
                    ) : option?.kind === 3 ? (
                        <ChannelCession color="#141516" />
                    ) : option?.kind === 4 ? (
                        <ChannelCollective color="#141516" />
                    ) : (
                        option?.kind === 5 && <ChannelOther color="#141516" />
                    )}
                    <Tooltip title={option.name.length > 40 ? option.name ?? "" : ""} arrow>
                        <span style={{ fontSize: "13px" }}>{truncateString(option.name, 40)}</span>
                    </Tooltip>
                </div>
                <div className={`add-sales-channel-type tag${option.type ?? ""}`}>
                    {option.type === 1
                        ? t("AddSales.FirstType")
                        : option.type === 2
                          ? t("AddSales.SecondType")
                          : option.type === 3
                            ? t("AddSales.ThirdType")
                            : null}
                </div>
            </li>
        );
    };

    return (
        <div className="add-specific-channel-modal-wrapper">
            <main className="add-specific-channel-main">
                <form>
                    <AutocompleteFormComponent
                        label={t("NewTitle.SelectChannel")}
                        control={control}
                        controlName="selectedChannel"
                        options={nonAddedChannels}
                        renderOption={renderChannelsOptions}
                        renderOptionText={(option) => `${option.name}`}
                        defaultFilterOptions
                    />
                    <StyledButton
                        onClick={() => onSubmitNewChannel()}
                        sx={buttonStyles}
                        disabled={!watchChannel?.name}
                        fullWidth
                    >
                        {t("NewTitle.Validate")}
                    </StyledButton>
                </form>
            </main>
        </div>
    );
};

export default AddSpecificChannel;

/** MUI STYLES */
const buttonStyles = {
    "&.MuiButton-root": {
        borderRadius: "5px",
        backgroundColor: "#303030",
        border: "none",
        color: "white",
        "&:hover": {
            backgroundColor: "#464646",
        },
        "&.Mui-disabled": {
            backgroundColor: "#E9E9E9",
        },
    },
};
